// @flow
import * as React from 'react';
import { RequiredActionProps } from '../../types';
import { Button, ButtonBase, Stack, Typography } from '@mui/material';
import moment from 'moment/moment';
import { ArrowsLeftRightIcon, ArrowSquareOutIcon, XIcon } from '@phosphor-icons/react';
import {
  AgentView, Portal, useAgentByIdQuery,
  useAppointmentReminderRequiredRescheduleOnAnotherAgentConfirmByCsrMutation, useInterval, UserAvatar
} from '../../../../shared';
import { useMemo, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { environment } from '../../../../../environments/environment';
import { ScheduleACallButton } from '@coverright/features';
import { getIsCSR, keycloak } from '@coverright/shared/keycloak';

export const ArCsrReassignRequiredAction = ({item, onResolved, onHide}: RequiredActionProps) => {
  const details = useMemo(() => JSON.parse(item.detailsJson), [item.detailsJson]);
  const [isBeforeNow, setIsBeforeNow] = useState(true);
  const isCSR = getIsCSR(keycloak)
  const [processed] = useAppointmentReminderRequiredRescheduleOnAnotherAgentConfirmByCsrMutation({
    variables: {id: item.id},
  })
  const {data} = useAgentByIdQuery({
    variables: {
      id: details.agentId,
    },
    skip: !details.agentId
  });
  const agent = data?.agentById;

  useInterval(() => setIsBeforeNow(isBefore(details.startTime)), 5000);

  const complete = async () => {
    await processed();
    onResolved();
  }

  return (
    <Portal sx={{'&:hover': {border: '1px solid #d0d5e1' }, transition: 'all .3s', mb: 2, background: '#FFF', minWidth: 366 }}>
      <Stack gap={2} direction={'row'} alignItems={'center'} justifyContent={'space-between'}
             sx={{background: 'linear-gradient(137deg, rgba(247, 231, 186, 1) 0%, rgb(231 253 255) 70%)'}} p={'8px 12px'}>
        <Typography flex={1} variant={'h5'} fontWeight={500} noWrap>
          Agent requested reassignment
        </Typography>

        <Typography variant={'caption'} align={'right'}>
          {moment(item.createdAt).format('L H:mm')}
        </Typography>

        {onHide && <ButtonBase onClick={onHide}>
          <XIcon size={16} color={'#1c434f'} weight={'bold'} />
        </ButtonBase>}
      </Stack>

      <Typography flex={1} p={1} variant={'body2'}>
        The agent asked to reassign the appointment for another agent.<br/>
        <Typography variant={'caption'} color={'text.secondary'}>Appointment was scheduled for: <b>{moment(details.startTime).format('L H:mm')}</b></Typography>
      </Typography>

      <Stack gap={1} direction={'row'} alignItems={'center'} pr={1}>
        <ButtonBase onClick={() => window.open(`/${'client'}/${details.invitee?.clientId}`, '_blank')}
                    sx={{
                      padding: '4px 8px',
                      borderRadius: '8px'
                    }}
        >
          <UserAvatar size={32}
                      fontSize={13}
                      showEmail
                      email={details.invitee?.email}
                      name={details.invitee?.name} />
        </ButtonBase>

        <ArrowsLeftRightIcon size={32} color={'#70a6b6'} weight={'bold'} />

        <AgentView agent={agent} />
      </Stack>
      <Stack p={1} justifyContent={'flex-end'} gap={1} direction={'row'}>
        {!isBeforeNow && <Button sx={{ width: 150 }} size={'small'}
                 onClick={() => window.open(`https://calendly.com/cancellations/${details.invitee?.uri.split('/').pop() || ''}`)}
                 endIcon={<ArrowSquareOutIcon />}
                 variant={'outlined'}>
          Cancel meeting
        </Button>}
        <ScheduleACallButton
          variant={'outlined'}
          size={'small'}
          sx={{width: 180}}
          fullWidth
          calendlyProps={{
            dataUrl: environment.production && isCSR ? 'https://calendly.com/coverright-advisors/planning' : undefined,
            agent,
            client: {
              firstName: details.invitee?.name,
              email: details.invitee?.email,
              phoneNumber: details.invitee?.phoneNumber,
            },
            onEventScheduled: complete
          }}>
          Schedule new call
        </ScheduleACallButton>

        <LoadingButton sx={{width: 100}} size={'small'} variant={'contained'} onClick={complete}>
          Completed
        </LoadingButton>
      </Stack>
    </Portal>
  );
};

const isBefore = (date: string) => moment(date).isBefore(moment());
